import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  MessageCircle, 
  Instagram, 
  DollarSign,
  TrendingUp,
  Users,
  Download,
  Share2,
  CreditCard,
  Target,
  CheckCircle,
  AlertCircle,
  Play,
  RefreshCw
} from 'lucide-react';

interface Metrics {
  totalUsers: number;
  kitDownloads: number;
  kitDownloadRate: number;
  proMapClicks: number;
  proMapClickRate: number;
  checkouts: number;
  checkoutRate: number;
  storyPosts: number;
  conversionFunnel: {
    instagram_dms: number;
    whatsapp_optins: number;
    kit_downloads: number;
    pro_map_clicks: number;
    checkouts: number;
    story_posts: number;
  };
  targets: {
    kitDownloadTarget: number;
    proMapClickTarget: number;
    checkoutTarget: number;
    storyPostTarget: number;
  };
  success: {
    kitDownloadRate: { current: number; target: number; met: boolean };
    proMapClickRate: { current: number; target: number; met: boolean };
    checkouts: { current: number; target: number; met: boolean };
    storyPosts: { current: number; target: number; met: boolean };
  };
  overallSuccess: boolean;
}

interface Config {
  testMode: boolean;
  keywords: { en: string[]; ptBr: string[] };
  cities: Array<{ id: string; name: string; country: string; language: string }>;
  vibes: Array<{ id: string; name: string; emoji: string; description: string }>;
  pricing: { freeKit: number; proMap: number; insiderDrops: number };
  instagramHandle: string;
}

const HiddenGemsDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [runningTest, setRunningTest] = useState(false);

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE}/api/hidden-gems/config`),
        fetch(`${API_BASE}/api/hidden-gems/metrics`)
      ]);

      const configData = await configRes.json();
      const metricsData = await metricsRes.json();

      setConfig(configData);
      setMetrics(metricsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runFullFlowTest = async () => {
    setRunningTest(true);
    try {
      const response = await fetch(`${API_BASE}/api/hidden-gems/test/full-flow`, {
        method: 'POST'
      });
      const result = await response.json();
      setTestResults(result);
    } catch (error) {
      console.error('Test failed:', error);
      setTestResults({ success: false, error: 'Test execution failed' });
    } finally {
      setRunningTest(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getSuccessColor = (met: boolean) => met ? 'text-green-400' : 'text-red-400';
  const getSuccessIcon = (met: boolean) => met ? CheckCircle : AlertCircle;

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading Hidden Gems Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
                <MapPin className="w-10 h-10 text-purple-400" />
                Hidden Gems City Pack
              </h1>
              <p className="text-gray-300">
                DM MAP/MAPA → WhatsApp Bot → Instant Kit → Viral Loop
              </p>
            </div>
            {config?.testMode && (
              <div className="bg-yellow-900/30 border border-yellow-600 px-4 py-2 rounded-lg">
                <span className="text-yellow-400 font-medium">🧪 TEST MODE</span>
              </div>
            )}
          </div>

          {/* Core Loop */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              Viral Loop Process
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Instagram className="w-4 h-4 text-pink-400" />
                IG Reel + DM MAP
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <MessageCircle className="w-4 h-4 text-green-400" />
                WhatsApp Bot
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Download className="w-4 h-4 text-blue-400" />
                Instant Kit Delivery
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Share2 className="w-4 h-4 text-yellow-400" />
                Story Share → Bonus
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
          <button
            onClick={runFullFlowTest}
            disabled={runningTest}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Play className={`w-4 h-4 ${runningTest ? 'animate-pulse' : ''}`} />
            {runningTest ? 'Testing...' : 'Test Full Flow'}
          </button>
        </div>

        {/* Success Metrics Overview */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-400" />
                  <span className="text-gray-300 text-sm">Kit Downloads</span>
                </div>
                {React.createElement(getSuccessIcon(metrics.success.kitDownloadRate.met), {
                  className: `w-5 h-5 ${getSuccessColor(metrics.success.kitDownloadRate.met)}`
                })}
              </div>
              <div className="text-2xl font-bold text-white">
                {metrics.kitDownloadRate}%
              </div>
              <div className="text-xs text-gray-400">
                Target: {metrics.success.kitDownloadRate.target}%
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  <span className="text-gray-300 text-sm">Pro Map Clicks</span>
                </div>
                {React.createElement(getSuccessIcon(metrics.success.proMapClickRate.met), {
                  className: `w-5 h-5 ${getSuccessColor(metrics.success.proMapClickRate.met)}`
                })}
              </div>
              <div className="text-2xl font-bold text-white">
                {metrics.proMapClickRate}%
              </div>
              <div className="text-xs text-gray-400">
                Target: {metrics.success.proMapClickRate.target}%
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-yellow-400" />
                  <span className="text-gray-300 text-sm">Checkouts</span>
                </div>
                {React.createElement(getSuccessIcon(metrics.success.checkouts.met), {
                  className: `w-5 h-5 ${getSuccessColor(metrics.success.checkouts.met)}`
                })}
              </div>
              <div className="text-2xl font-bold text-white">
                {metrics.checkouts}
              </div>
              <div className="text-xs text-gray-400">
                Target: {metrics.success.checkouts.target}+
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-pink-400" />
                  <span className="text-gray-300 text-sm">Story Posts</span>
                </div>
                {React.createElement(getSuccessIcon(metrics.success.storyPosts.met), {
                  className: `w-5 h-5 ${getSuccessColor(metrics.success.storyPosts.met)}`
                })}
              </div>
              <div className="text-2xl font-bold text-white">
                {metrics.storyPosts}
              </div>
              <div className="text-xs text-gray-400">
                Target: {metrics.success.storyPosts.target}+
              </div>
            </div>
          </div>
        )}

        {/* Conversion Funnel */}
        {metrics && (
          <div className="bg-gray-800 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Conversion Funnel
            </h3>
            <div className="space-y-4">
              {Object.entries(metrics.conversionFunnel).map(([step, count], index) => {
                const percentage = index === 0 ? 100 : Math.round((count / metrics.conversionFunnel.instagram_dms) * 100);
                const stepNames: { [key: string]: string } = {
                  instagram_dms: 'Instagram DMs (MAP/MAPA)',
                  whatsapp_optins: 'WhatsApp Opt-ins',
                  kit_downloads: 'Free Kit Downloads',
                  pro_map_clicks: 'Pro Map Clicks ($5)',
                  checkouts: 'Completed Purchases',
                  story_posts: 'Story Posts (Viral)'
                };

                return (
                  <div key={step} className="flex items-center gap-4">
                    <div className="w-48 text-gray-300 text-sm">
                      {stepNames[step]}
                    </div>
                    <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000"
                        style={{ width: `${percentage}%` }}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium text-white">
                        <span>{count}</span>
                        <span>{percentage}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults && (
          <div className="bg-gray-800 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-green-400" />
              Full Flow Test Results
            </h3>
            {testResults.success ? (
              <div className="space-y-3">
                <div className="text-green-400 font-medium">✅ All 7 steps completed successfully!</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="text-blue-400 font-medium">1. IG DM MAP</div>
                    <div className="text-gray-300">Auto-reply with WhatsApp link</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="text-green-400 font-medium">2-4. WhatsApp Bot</div>
                    <div className="text-gray-300">City + vibe collection</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <div className="text-purple-400 font-medium">5. Kit Delivery</div>
                    <div className="text-gray-300">PDF + map + story template</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-red-400">❌ Test failed: {testResults.error}</div>
            )}
          </div>
        )}

        {/* Success Status */}
        {metrics && (
          <div className={`${metrics.overallSuccess ? 'bg-green-900/30 border-green-600' : 'bg-yellow-900/30 border-yellow-600'} border rounded-xl p-6`}>
            <div className="flex items-center gap-3">
              {metrics.overallSuccess ? (
                <CheckCircle className="w-6 h-6 text-green-400" />
              ) : (
                <AlertCircle className="w-6 h-6 text-yellow-400" />
              )}
              <div>
                <div className={`font-bold text-lg ${metrics.overallSuccess ? 'text-green-400' : 'text-yellow-400'}`}>
                  {metrics.overallSuccess ? 'MVP SUCCESS! 🎉' : 'Getting Close...'}
                </div>
                <div className="text-gray-300 text-sm">
                  {metrics.overallSuccess 
                    ? 'All success metrics exceeded! Ready for real launch.' 
                    : 'Some targets not met yet. Keep testing and iterating.'
                  }
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default HiddenGemsDashboard;